import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CartProvider } from './contexts/CartContext';
import ProtectedRoute from './components/ProtectedRoute';
import MicrosoftRedirectHandler from './components/MicrosoftRedirectHandler';
import EduGenieWidget from './components/edugenie/EduGenieWidget';
import { isTenantDomain } from './utils/tenant'; // [TENANT]

// Kept in the main bundle: entry routes needed for first paint
import LandingPage from './pages/Landing/LandingPage';
import Login from './pages/Auth/Login';

// Lazy-loaded: role areas (biggest savings)
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const UserLayout = lazy(() => import('./user/UserLayout'));
const OrganizationLayout = lazy(() => import('./organization/Layout'));
const InstructorLayout = lazy(() => import('./instructor/Layout'));
const MasterAdminLayout = lazy(() => import('./masteradmin/Layout'));
const ManagerLayout = lazy(() => import('./manager/Layout'));

// Lazy-loaded: secondary public pages
const PrivacyPolicyPage = lazy(() => import('./pages/Landing/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/Landing/TermsOfUsePage'));
const FAQPage = lazy(() => import('./pages/Landing/FAQPage'));
const TwoFactorSetup = lazy(() => import('./pages/Auth/TwoFactorSetup'));
const TwoFactorVerify = lazy(() => import('./pages/Auth/TwoFactorVerify'));
const RegisterPage = lazy(() => import('./pages/Landing/RegisterPage'));
const EnquiryPage = lazy(() => import('./pages/Landing/EnquiryPage'));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/Auth/ResetPasswordPage'));

const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    <div className="spinner-border text-primary" role="status"></div>
  </div>
);

// [TENANT] True on a client subdomain such as igi.ethosboard.com.
// The address bar can't change without a full page load, so reading it once is enough.
const ON_TENANT = isTenantDomain();

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <NotificationProvider>
          <CartProvider>
            <Router>
              <MicrosoftRedirectHandler />

              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}

                  {/* [TENANT] Home: landing page on the main site (ethosboard.com),
                      straight to login on a client subdomain (igi.ethosboard.com) */}
                  <Route
                    path="/"
                    element={ON_TENANT ? <Navigate to="/login" replace /> : <LandingPage />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/2fa/setup" element={<TwoFactorSetup />} />
                  <Route path="/2fa/verify" element={<TwoFactorVerify />} />

                  {/* [TENANT] Self-registration and enquiries only make sense on the main site */}
                  <Route
                    path="/register"
                    element={ON_TENANT ? <Navigate to="/login" replace /> : <RegisterPage />}
                  />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                  <Route
                    path="/enquiry"
                    element={ON_TENANT ? <Navigate to="/login" replace /> : <EnquiryPage />}
                  />
                  <Route path="/courses" element={<Navigate to="/" replace />} />
                  <Route path="/courses/:slug" element={<Navigate to="/" replace />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                  <Route path="/faq" element={<FAQPage />} />

                  {/* Protected Admin routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Master Admin routes (ProtectedRoute currently disabled) */}
                  <Route
                    path="/masteradmin/*"
                    element={
                      // <ProtectedRoute requiredRole="masteradmin">
                      <MasterAdminLayout />
                      // </ProtectedRoute>
                    }
                  />

                  {/* Protected User routes */}
                  <Route
                    path="/user/*"
                    element={
                      <ProtectedRoute requiredRole="user">
                        <UserLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Organization Admin routes */}
                  <Route
                    path="/organization/*"
                    element={
                      <ProtectedRoute requiredRole="organization">
                        <OrganizationLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Manager routes */}
                  <Route
                    path="/manager/*"
                    element={
                      <ProtectedRoute requiredRole="manager">
                        <ManagerLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Instructor routes */}
                  <Route
                    path="/instructor/*"
                    element={
                      <ProtectedRoute requiredRole="instructor">
                        <InstructorLayout />
                      </ProtectedRoute>
                    }
                  />

                  {/* Fallback redirects ("/" sends client subdomains on to /login) */}
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>

              <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </Router>
          </CartProvider>
        </NotificationProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
